import React, { useState, useEffect } from 'react';
import { formatAppDateTime } from '../../services/appConfigRuntime';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Refresh,
  Delete,
  Close,
} from '@mui/icons-material';
import { pdfApiService } from '../../services/pdfApi';
import { toast } from 'react-toastify';

const KB_ALLOWED_EXTENSIONS = [
  'pdf',
  'docx',
  'xlsx',
  'xls',
  'csv',
  'png',
  'jpg',
  'jpeg',
  'webp',
  'gif',
  'bmp',
  'tiff',
  'tif',
];

const KB_ACCEPT_ATTR = '.pdf,.docx,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tiff,.tif';

const KB_FORMATS_LABEL =
  'PDF, Word (DOCX), Excel (XLS/XLSX), CSV, and images (PNG, JPG, WEBP, GIF, BMP, TIFF)';

const isKbAllowedFile = (file) => {
  const extension = file.name.split('.').pop().toLowerCase();
  return KB_ALLOWED_EXTENSIONS.includes(extension);
};

const isImageFile = (filename = '') => {
  const extension = filename.split('.').pop().toLowerCase();
  return ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'tiff', 'tif'].includes(extension);
};

const PDFHub = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch files on component mount and when refresh is triggered
  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await pdfApiService.listFiles();
      if (response.data && response.data.files) {
        setFiles(response.data.files);
        setPage(0); // Reset to first page when files are fetched
      }
    } catch (error) {
      console.error('Error fetching PDF files:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const addSelectedFiles = (incomingFiles) => {
    const validFiles = incomingFiles.filter(isKbAllowedFile);
    if (validFiles.length !== incomingFiles.length) {
      toast.warning(`Some files were skipped. Allowed: ${KB_FORMATS_LABEL}.`);
    }
    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const handleFileSelect = (event) => {
    addSelectedFiles(Array.from(event.target.files));
    event.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);

    const droppedFiles = Array.from(event.dataTransfer.files);
    addSelectedFiles(droppedFiles);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const formData = new FormData();
        formData.append('file', file);

        await pdfApiService.upload(formData, (progress) => {
          // Calculate overall progress across all files
          const overallProgress = ((i * 100 + progress) / selectedFiles.length);
          setUploadProgress(overallProgress);
        });

        toast.success(`Successfully uploaded ${file.name}`);
      }

      // Reset and close dialog
      setSelectedFiles([]);
      setUploadDialogOpen(false);
      setUploadProgress(0);
      
      // Refresh file list
      fetchFiles();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleViewFile = async (file) => {
    setSelectedFile(file);
    setViewerOpen(true);
    setViewerUrl(null);

    try {
      const response = await pdfApiService.getFileUrl(file.key);
      if (response.data && response.data.url) {
        setViewerUrl(response.data.url);
      } else {
        toast.error('Failed to generate file URL');
      }
    } catch (error) {
      console.error('Error getting file URL:', error);
      toast.error('Failed to load file');
    }
  };

  const handleRowClick = (file) => {
    handleViewFile(file);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    try {
      return formatAppDateTime(dateString);
    } catch {
      return dateString;
    }
  };

  return (
    <Box 
      sx={{ 
        height: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 3
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
          PDF Hub
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <IconButton
            onClick={fetchFiles}
            disabled={loading}
            color="primary"
            title="Refresh"
          >
            <Refresh />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Document
          </Button>
        </Box>
      </Box>

      {/* File Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexGrow: 1 }}>
          <CircularProgress />
        </Box>
      ) : files.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No documents uploaded yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click "Upload Document" to get started
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <TableContainer 
            sx={{ 
              flexGrow: 1,
              width: '100%',
              overflow: 'auto'
            }}
          >
            <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '60%' }}>File Name</TableCell>
                  <TableCell sx={{ width: '15%' }}>Size</TableCell>
                  <TableCell sx={{ width: '25%' }}>Upload Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((file, index) => (
                    <TableRow
                      key={index}
                      onClick={() => handleRowClick(file)}
                      sx={{
                        cursor: 'pointer',
                        height: 53, // Fixed row height
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell sx={{ width: '60%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Description sx={{ color: 'primary.main', flexShrink: 0 }} />
                          <Typography variant="body2" noWrap>
                            {file.filename}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '15%' }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(file.size)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '25%' }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(file.last_modified)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                {/* Fill empty rows to maintain fixed height */}
                {Array.from({ length: Math.max(0, rowsPerPage - files.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length) }).map((_, index) => (
                  <TableRow key={`empty-${index}`} sx={{ height: 53 }}>
                    <TableCell colSpan={3} sx={{ border: 'none' }} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={files.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{ flexShrink: 0 }}
          />
        </Paper>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload Documents
          <IconButton
            aria-label="close"
            onClick={() => !uploading && setUploadDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            disabled={uploading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Drag and Drop Area */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('pdf-file-upload').click()}
            sx={{
              border: '2px dashed',
              borderColor: isDragOver ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: isDragOver ? 'primary.light' : 'grey.50',
              transition: 'all 0.3s ease',
              mb: 3,
              cursor: 'pointer',
            }}
          >
            <CloudUpload
              sx={{
                fontSize: 48,
                color: isDragOver ? 'primary.main' : 'grey.400',
                mb: 2
              }}
            />
            <Typography variant="h6" gutterBottom>
              {isDragOver ? 'Drop files here' : 'Drag & Drop files here'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              or click to select files
            </Typography>

            <input
              accept={KB_ACCEPT_ATTR}
              style={{ display: 'none' }}
              id="pdf-file-upload"
              multiple
              type="file"
              onChange={handleFileSelect}
            />
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUpload />}
              disabled={uploading}
              onClick={(e) => {
                e.stopPropagation();
                document.getElementById('pdf-file-upload').click();
              }}
            >
              Select Files
            </Button>

            <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
              Supported formats: {KB_FORMATS_LABEL}
            </Typography>
          </Box>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Selected Files ({selectedFiles.length})
              </Typography>
              {selectedFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <Description sx={{ mr: 1, color: 'primary.main' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.size)}
                      </Typography>
                    </Box>
                  </Box>
                  {!uploading && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Upload Progress */}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Uploading... {Math.round(uploadProgress)}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadDialogOpen(false);
              setSelectedFiles([]);
              setUploadProgress(0);
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={selectedFiles.length === 0 || uploading}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedFile?.filename}
            </Typography>
            <IconButton onClick={() => setViewerOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {viewerUrl ? (
            isImageFile(selectedFile?.filename) ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  p: 2,
                  backgroundColor: 'grey.100',
                }}
              >
                <Box
                  component="img"
                  src={viewerUrl}
                  alt={selectedFile?.filename || 'Uploaded image'}
                  sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              </Box>
            ) : (
              <iframe
                src={viewerUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                title="Document Viewer"
              />
            )
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PDFHub;

